import * as React from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import { cn } from "../../lib/utils";

const DialogContext = React.createContext(null);

function Dialog({ open, onOpenChange, children }) {
  return (
    <DialogContext.Provider value={{ open, onOpenChange }}>
      {children}
    </DialogContext.Provider>
  );
}

function DialogTrigger({ children, asChild, ...props }) {
  const ctx = React.useContext(DialogContext);
  const onClick = (e) => {
    props.onClick?.(e);
    ctx?.onOpenChange?.(true);
  };
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, { onClick });
  }
  return (
    <button type="button" onClick={onClick} {...props}>
      {children}
    </button>
  );
}

function DialogContent({ children, className, showClose = true, ...props }) {
  const ctx = React.useContext(DialogContext);
  const open = ctx?.open;
  const onOpenChange = ctx?.onOpenChange;

  React.useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onOpenChange?.(false);
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  const content = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in-0"
        onClick={() => onOpenChange?.(false)}
        aria-hidden="true"
      />
      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          "relative z-10 flex max-h-[85vh] w-full max-w-[640px] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_16px_40px_rgba(0,0,0,0.10)] animate-in fade-in-0 zoom-in-95 duration-200",
          className
        )}
        {...props}
      >
        {showClose && (
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            className="absolute right-3.5 top-3.5 z-20 flex h-8 w-8 items-center justify-center rounded-full border border-gray-100 bg-white text-gray-500 shadow-sm hover:bg-gray-50 hover:text-gray-700 transition"
            aria-label="Close"
          >
            <Icon icon="mdi:close" className="h-4 w-4" />
          </button>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

function DialogHeader({ className, ...props }) {
  return <div className={cn("shrink-0 border-b border-gray-100 bg-white px-6 pt-6 pb-4 sm:px-7", className)} {...props} />;
}

function DialogTitle({ className, children, ...props }) {
  return (
    <h2 className={cn("pr-8 text-[18px] font-semibold leading-none tracking-tight text-gray-900", className)} {...props}>
      {children}
    </h2>
  );
}

function DialogDescription({ className, ...props }) {
  return <p className={cn("pr-6 text-[13px] leading-6 text-gray-500", className)} {...props} />;
}

function DialogBody({ className, ...props }) {
  return (
    <div
      className={cn(
        "flex-1 overflow-y-auto px-6 py-5 sm:px-7 text-[14px] leading-7 text-gray-600 [scrollbar-width:thin] [scrollbar-color:theme(colors.gray.200)_transparent] [&::-webkit-scrollbar]:w-[6px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-track]:bg-transparent",
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }) {
  return (
    <div
      className={cn(
        "shrink-0 flex flex-col-reverse gap-2 border-t border-gray-100 bg-white px-6 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7",
        className
      )}
      {...props}
    />
  );
}

export { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter };
